/* eslint-disable react/prop-types */
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  ExpandMore as ExpandMoreIcon,
  ReportGmailerrorred as ReportGmailerrorredIcon,
  Edit as EditIcon,
  LinkOff as LinkOffIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import HazardDetails from "./HazardDetails";

function HazardAccordion({
  hazard,
  isExpanded,
  onToggle,
  onEdit,
  onUnlink,
  onDelete,
  showUnlinkButton,
  getRiskLevelColor,
  onUnlinkPrecaution,
  deletingPrecaution,
  onAddPrecaution,
  onAddPrecautionWithCategory,
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Accordion
      expanded={isExpanded}
      onChange={(event, expanded) => onToggle(expanded)}
      sx={{
        mb: 1.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        transition: "all 0.3s ease",
        overflow: "hidden",
        "&:hover": {
          borderColor: "warning.main",
          boxShadow: theme.shadows[4],
        },
        "&:before": {
          display: "none",
        },
        "&.Mui-expanded": {
          margin: "0 0 12px 0",
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.6)} 100%)`,
          "&.Mui-expanded": {
            borderRadius: 0,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            gap: 2,
          }}
        >
          <Avatar
            sx={{
              bgcolor: alpha(theme.palette.warning.main, 0.1),
              border: "2px solid",
              borderColor: alpha(theme.palette.warning.main, 0.2),
              width: 40,
              height: 40,
            }}
          >
            <ReportGmailerrorredIcon
              sx={{
                color: "warning.main",
                fontSize: 20,
              }}
            />
          </Avatar>
          <Typography
            variant="body1"
            fontWeight={isExpanded ? "700" : "500"}
            color="text.primary"
            sx={{
              flex: 1,
              transition: "font-weight 0.3s ease",
            }}
          >
            {hazard.name}
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box sx={{ display: "flex", gap: 0.5, ml: 1 }}>
              <Tooltip title={t("hazards.editTooltip")}>
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                  sx={{
                    background: "none",
                    padding: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "primary.main",
                    borderRadius: 1,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      transform: "scale(1.1)",
                    },
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={t("hazards.editButtonAria", {
                    name: hazard.name,
                  })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.stopPropagation();
                      onEdit();
                    }
                  }}
                >
                  <EditIcon />
                </Box>
              </Tooltip>
              {showUnlinkButton && (
                <Tooltip title={t("hazards.unlinkTooltip")}>
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      onUnlink();
                    }}
                    sx={{
                      background: "none",
                      padding: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      color: "warning.main",
                      borderRadius: 1,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        bgcolor: alpha(theme.palette.warning.main, 0.1),
                        transform: "scale(1.1)",
                      },
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={t("hazards.unlinkButtonAria", {
                      hazardName: hazard.name,
                    })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.stopPropagation();
                        onUnlink();
                      }
                    }}
                  >
                    <LinkOffIcon />
                  </Box>
                </Tooltip>
              )}
              <Tooltip title={t("hazards.deleteTooltip")}>
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                  sx={{
                    background: "none",
                    padding: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "error.main",
                    borderRadius: 1,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.error.main, 0.1),
                      transform: "scale(1.1)",
                    },
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={t("hazards.deleteButtonAria", {
                    name: hazard.name,
                  })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.stopPropagation();
                      onDelete();
                    }
                  }}
                >
                  <DeleteIcon />
                </Box>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 3, pt: 2 }}>
        <HazardDetails
          hazard={hazard}
          getRiskLevelColor={getRiskLevelColor}
          onUnlinkPrecaution={onUnlinkPrecaution}
          deletingPrecaution={deletingPrecaution}
          onAddPrecaution={onAddPrecaution}
          onAddPrecautionWithCategory={onAddPrecautionWithCategory}
        />
      </AccordionDetails>
    </Accordion>
  );
}

export default HazardAccordion;
